<script setup lang="ts">
import vue, { ref, onMounted } from 'vue';
import router from '@/router';
import { useRoute } from 'vue-router';
import { IonButton, IonContent, IonList } from '@ionic/vue';
import { useI18n } from 'vue-i18n';

import { Address } from '@/shared/interfaces/wallet';
import { TransactionItem } from '@/shared/interfaces/transaction';
import { TransactionType } from '@/enums/wallet.enum';
import { useCheckout } from '@/shared/composables/useCheckout';
import hexRgb from 'hex-rgb';
import ItemsDetails from '@/shared/components/ui/ItemsDetails.vue';
import rgbHex from 'rgb-hex';
import TextInput from '@/shared/components/sign-up-forms/TextInput.vue';
import TopBar from '@/shared/components/TopBar.vue';
import useUserProfile from '@/modules/profile/composables/useUserProfile';
import useUtils from '@/shared/composables/useUtils';
import useWallet from '@/modules/wallet/composables/useWallet';
import { useAuthStore } from '@/stores/auth/authStore';
import { Wallet } from '@/shared/interfaces/balance';
import { Product } from '@/shared/interfaces/tableSpot';
import { useTransactionStore } from '@/stores/transaction/transactionStore';
import useTenant from '@/shared/composables/useTenant';
import useCurrencies from '@/shared/composables/useCurrencies';
import { useSharedStore } from '@/stores/shared/sharedStore';
import BackButton from '@/shared/components/BackButton.vue';

// Init
const { t } = useI18n();
const { getTenantConfigAndStyles } = useTenant();
const { tenantConfig } = getTenantConfigAndStyles();
const emit = defineEmits(['onFormValidation']);
const { getUserProfile } = useUserProfile();
const { userProfile } = getUserProfile();
const route = useRoute();
const transactionType = route.query.transactionType;
const pendingTopUp = route.query.pendingTopUp;
const checkStock = transactionType === TransactionType.SELF_ORDER ? true : false;
const { calculateShade } = useUtils();
const primary_color = window.primary_color;
const primaryRgb = hexRgb(window.primary_color, { format: 'array' });
const primaryShade = '#' + rgbHex(calculateShade(primaryRgb.toString(), 0.2));
const buttonText = ref('');
const buttonDisabled = ref(true);
const formData: any = {
	quantity: 0,
	total: [],
};
const totalCurrenciesNotEnough: any = ref([]);
const totalItemsPriceString = ref(' ');
const totalPriceInEur = ref('');
const transactionStore = useTransactionStore();
const authStore = useAuthStore();
const sharedStore = useSharedStore();
const { fiatCurrency } = useCurrencies();
const requiresLogin = ref(false);

// Check user's wallet balance
const { getWallets } = useWallet();
const { refetchWalletsData, wallets } = getWallets();

const { items, totalItemCount, handleCheckout, handleCheckoutWithForm, totalItemsPrice, getTotalPricePerCurrency, billingAddressValidationSchema } = useCheckout();

const address: Address = {
	country: userProfile.value?.country ?? 'Belgium',
	city: userProfile.value?.city ?? '',
	address_street: userProfile.value?.address_street ?? '',
	address_number: userProfile.value?.address_number ?? '',
	street: userProfile.value?.address_street ?? '',
	number: userProfile.value?.address_number ?? '',
	postal_code: userProfile.value?.postal_code ?? '',
};

// Go back
const goToLogin = () => {
	console.log('[CheckoutPage] Navigating to login. Saving redirect:', route.fullPath);
	sharedStore.pendingCheckoutRedirect = route.fullPath;
	router.push('/auth/signinhome');
};
const goBack = () => {
	if (pendingTopUp) {
		router.push('/dashboard');
	} else {
		router.back();
	}
};

// Validating form
const onInputChange = (formValid: vue.ComputedRef<boolean>) => {
	emit('onFormValidation', formValid);
};

onMounted(async () => {
	const { isAuthenticated } = useAuthStore();

	if (isAuthenticated()) {
		await refetchWalletsData();
	}

	const totalPricePerCurrency = getTotalPricePerCurrency();
	if (isAuthenticated()) {
		if (wallets && wallets.value) {
			const totalCurrenciesNotEnough: any = [];

			// Handle Self Order Products
			if (transactionType === TransactionType.SELF_ORDER) {
				const filteredProducts = items.filter((item) => item.additionalInfo.id !== 'administrative_fee');
				filteredProducts.forEach((item) => {
					const product = new Product(item.additionalInfo.product);

					// Check if the product has only one price
					// and user has a wallet for that currency
					const wallet = wallets.value?.find((wallet) => wallet.currency.id === product.currency.id);
					const isOnePrice = product.prices.length === 1;
					if (wallet && isOnePrice) {
						// Check if user has enough balance for that product
						// If not, disable the button and show a message
						const subtotal = item.unitPrice! * item.quantity;
						const totalPriceForCurrency = totalPricePerCurrency.find((currency: any) => currency.currencyId === product.currency.id);

						if (totalPriceForCurrency && totalPriceForCurrency.total > +wallet.current_balance && product.currency.topupable) {
							// Set the administrative fee as this is require a top up
							// This will be added just once
							transactionStore.setAdministrativeFee(+tenantConfig.value!.top_up_cost, t('transaction.administrative_fee'));

							const currency = totalCurrenciesNotEnough.find((currency: any) => currency.wallet.currency.id === wallet.currency.id);
							if (!currency) {
								// If wallet currency is the fiat currency, we add the administrative fee to the total
								const calculatedSubtotal = wallet.currency.name === fiatCurrency.value.nameId ? subtotal + +tenantConfig.value!.top_up_cost : subtotal;

								totalCurrenciesNotEnough.push({
									subtotal: calculatedSubtotal,
									wallet: wallet,
								});
							} else {
								currency.subtotal += subtotal;
							}
							buttonDisabled.value = true;
						} else {
							// If user has enough balance, but as it is only one currency, we left the user to top up and pay
							// Set the administrative fee as this is require a top up
							// transactionStore.setAdministrativeFee(+tenantConfig.value!.top_up_cost, t('transaction.administrative_fee'));
						}
					} else if (wallet && !isOnePrice) {
						// Product has multiple prices: We left backend to resolve this
						console.log('Wallet was found but product has multiple prices');
					} else {
						// Wallet was not found
						console.log('Wallet not found');
					}
				});
			}
			if (transactionType === TransactionType.PAYMENT || transactionType === 'QUEUE_BOOKING' || transactionType === 'QUEUE_UPSELL') {
				totalPricePerCurrency.forEach((currencyTotal) => {
					// Check if user has a wallet for that currency
					const wallet = wallets.value?.find((wallet) => wallet.currency.id === currencyTotal.currencyId);

					if (wallet) {
						// Check if user has enough balance for that product
						// If not, disable the button and show a message
						if (currencyTotal.total > +wallet.current_balance && wallet.currency.topupable) {
							// Set the administrative fee as this is require a top up
							// This will be added just once
							transactionStore.setAdministrativeFee(+tenantConfig.value!.top_up_cost, t('transaction.administrative_fee'));

							const currencyNotEnough = totalCurrenciesNotEnough.find((currency: any) => currency.wallet.currency.id === wallet.currency.id);
							if (!currencyNotEnough) {
								const calculatedSubtotal = currencyTotal.total;

								totalCurrenciesNotEnough.push({
									subtotal: calculatedSubtotal,
									wallet: wallet,
								});
							} else {
								currencyNotEnough.subtotal += currencyTotal.total;
							}
							buttonDisabled.value = true;
						} else {
							// If user has enough balance, but as it is only one currency, we left the user to top up and pay
							// Set the administrative fee as this is require a top up
							// transactionStore.setAdministrativeFee(+tenantConfig.value!.top_up_cost, t('transaction.administrative_fee'));
						}
					} else {
						// Wallet was not found
						console.log('Wallet not found');
					}
				});
			}

			if (totalCurrenciesNotEnough.length === 1 && totalCurrenciesNotEnough[0].wallet.currency.topupable) {
				formData.quantity = totalPricePerCurrency.length;
				formData.total = totalPricePerCurrency.map((currency) => {
					return {
						currency_id: currency.currencyId,
						amount: currency.total,
					};
				});
				const currency = totalCurrenciesNotEnough[0];
				const totalToTopUp = Math.abs(+currency.wallet.current_balance - currency.subtotal);

				buttonText.value = t('checkout.pay_now');

				buttonDisabled.value = false;
				if (totalToTopUp > 0) {
					const currency = totalCurrenciesNotEnough[0].wallet.currency;
					if (currency.name === fiatCurrency.value.nameId) {
						formData.top_up_amount = totalToTopUp.toFixed(2);
					} else {
						formData.total = formData.total.filter((item: any) => item.currency_id);
						formData.top_up_amount = totalToTopUp.toFixed(2);
					}
				}
				if (+formData.top_up_amount < 1) {
					buttonText.value = t('top_up.minimum_top_up_amount'); // Inform the user
					buttonDisabled.value = true;
				}
			} else if (totalCurrenciesNotEnough.length > 1) {
				buttonText.value = t('error.insufficient_balance'); // Inform the user
				buttonDisabled.value = true;
			} else {
				formData.quantity = totalPricePerCurrency.length;
				formData.total = totalPricePerCurrency.map((currency) => {
					return {
						currency_id: currency.currencyId,
						amount: currency.total,
					};
				});
				buttonText.value = t('checkout.pay_now');
				buttonDisabled.value = false;
			}
		}
	}
	if (!isAuthenticated()) {
		const isProfileMode = (tenantConfig.value?.enable_public_mode ?? false) === false && (tenantConfig.value?.enable_full_guest_mode ?? false) === false;
		const isSelfOrder = transactionType === TransactionType.SELF_ORDER;
		console.log('[CheckoutPage] User not authenticated');
		console.log('[CheckoutPage] isProfileMode:', isProfileMode, '| enable_public_mode:', tenantConfig.value?.enable_public_mode, '| enable_full_guest_mode:', tenantConfig.value?.enable_full_guest_mode);
		console.log('[CheckoutPage] isSelfOrder:', isSelfOrder, '| transactionType:', transactionType);

		if (isProfileMode && isSelfOrder) {
			console.log('[CheckoutPage] PROFILE MODE + SELF ORDER -> Login required before payment');
			buttonText.value = t('checkout.login_and_pay');
			buttonDisabled.value = false;
			requiresLogin.value = true;
		} else {
			console.log('[CheckoutPage] PUBLIC/GUEST MODE -> Proceed as guest');
			formData.quantity = totalPricePerCurrency.length;
			formData.total = totalPricePerCurrency.map((currency) => {
				formData.top_up_amount = currency.total;
				return {
					currency_id: currency.currencyId,
					amount: currency.total,
				};
			});
			buttonText.value = t('checkout.pay_now');
			buttonDisabled.value = false;
		}
	}
	totalItemsPriceString.value = transactionStore.calculateTotalsPerProductCurrency();
	totalPriceInEur.value = transactionStore.calculateTotalInEur();
	transactionStore.totalPriceString = totalItemsPriceString.value;
});
</script>

<template>
	<ion-content :style="`background: linear-gradient(${primary_color}, ${primaryShade});`">
		<div class="flex flex-col justify-between px-[34px]">
			<div>
				<TopBar>
					<template #left>
						<BackButton :onClick="goBack" />
					</template>

					<template #center>
						<div class="flex grow items-center justify-center w-full">
							<div class="mr-2">
								<span class="font-sourcesanspro-semibold text-l2"></span>
							</div>
						</div>
					</template>
				</TopBar>

				<div class="flex flex-col items-center justify-center mb-8">
					<h1 class="font-sourcesanspro-semibold text-lg2">{{ t('checkout.title') }}</h1>
					<span class="font-sourcesanspro-regular text-base2 text-white/50">
						{{ items?.length > 0 ? (items?.length > 1 ? `${Number(totalItemCount)} ${t('checkout.items')}` : `${Number(totalItemCount)} ${t('checkout.item')}`) : t('checkout.no_item') }}
					</span>
				</div>

				<div class="flex flex-col w-full align-center">
					<ItemsDetails
						v-if="items?.length > 0"
						border-class="border-y border-y-white/5"
						class="backdrop-blur-xl bg-anykrowd-gray/20 text-white rounded-xl mb-4"
						text-class="text-white"
						text-class-two="text-white/70"
						:title="t('checkout.order_details')"
						:items="
							items.map((item: TransactionItem) => {
								return {
									discount: item.discount,
									itemId: item.itemId,
									title: item.description,
									hideQuantity: false,
									hidePrice: false,
									price: item.unitPrice,
									quantity: item.quantity,
									totalPrice: item.itemPrice,
									totalPriceCurrency: item.additionalInfo?.product?.currency?.prefix,
								};
							})
						"
						:total-title="items?.length > 0 ? Number(totalItemCount) + ` ${t('checkout.items_total')}` : t('checkout.empty')"
						:total-price-string="totalItemsPriceString || '0'"
						:total-price="totalItemsPrice"
						:total-price-in-eur="totalPriceInEur" />
				</div>

				<div v-if="authStore.isAuthenticated()" class="flex flex-col w-full align-center">
					<ItemsDetails
						v-if="wallets && wallets.length > 0"
						border-class="border-y border-y-white/5"
						class="backdrop-blur-xl bg-anykrowd-gray/20 text-white rounded-xl mb-4"
						text-class="text-white"
						text-class-two="text-white/70"
						:title="t('wallet.checkout.wallets_title')"
						:items="
							wallets.map((wallet: Wallet) => {
								return {
									itemId: wallet.id,
									title: wallet.currency.name,
									hideQuantity: true,
									quantity: 1,
									totalPrice: +wallet.current_balance,
									totalPriceCurrency: wallet.currency.prefix,
								};
							})
						"
						:show-totals="false" />
				</div>

				<ion-list v-if="transactionType === TransactionType.SHOP_ORDER" class="backdrop-blur-xl bg-anykrowd-gray/20 text-white rounded-xl mb-4">
					<div class="flex px-6 py-4 items-center text-white">
						<span class="font-sourcesanspro-semibold text-l2">{{ t('checkout.billing_address.title') }}</span>
					</div>
					<div class="border-y border-y-white/5 px-6">
						<form id="billingAddressForm" @submit="handleCheckoutWithForm" :validation-schema="billingAddressValidationSchema">
							<TextInput wrapper-class="flex flex-wrap justify-left item-center my-2 border border-solid focus-within:background-white-15" name="country" type="text" :value="address.country" :placeholder="$t('checkout.billing_address.country')" @inputChanged="onInputChange" />
							<TextInput wrapper-class="flex flex-wrap justify-left item-center my-2 border border-solid focus-within:background-white-15" name="city" type="text" :value="address.city" :placeholder="$t('checkout.billing_address.city')" @inputChanged="onInputChange" />
							<TextInput wrapper-class="flex flex-wrap justify-left item-center my-2 border border-solid focus-within:background-white-15" name="address_street" type="text" :value="address.address_street" :placeholder="$t('checkout.billing_address.street')" @inputChanged="onInputChange" />
							<TextInput wrapper-class="flex flex-wrap col-md-6 justify-left item-center my-2 border border-solid focus-within:background-white-15" name="address_number" type="text" :value="address.address_number" :placeholder="$t('checkout.billing_address.address_number')" @inputChanged="onInputChange" />
							<TextInput wrapper-class="flex flex-wrap col-md-6 justify-left item-center mb-3 mt-2 border border-solid focus-within:background-white-15" name="postal_code" type="text" :value="address.postal_code" :placeholder="$t('checkout.billing_address.postal_code')" @inputChanged="onInputChange" />
						</form>
					</div>
				</ion-list>

				<div class="mb-2" v-if="transactionType === TransactionType.PAYMENT">
					<p class="font-sourcesanspro-regular text-sm2 text-center text-white/50">
						{{ t('checkout.check') }}
					</p>
				</div>
			</div>

			<div class="mb-8">
				<ion-button v-if="transactionType === TransactionType.SHOP_ORDER" :disabled="totalItemsPrice === 0" class="w-full" expand="full" form="billingAddressForm" size="large" shape="round" type="submit">
					<div v-if="userWallet" class="flex w-full items-center justify-between">
						<div class="text-m2" v-if="+totalItemsPrice > +userWallet?.current_balance">
							{{ t('checkout.top_up') }}
							<span>{{ `€${(+totalItemsPrice - +userWallet?.current_balance).toFixed(2)}` }}</span>
							& {{ t('checkout.pay') }}
						</div>
						<span class="text-m2" v-else>{{ t('checkout.pay_now') }}</span>
						<div>
							<span>€{{ (+totalItemsPrice).toFixed(2) || 0 }}</span>
						</div>
					</div>
				</ion-button>

				<ion-button v-if="transactionType !== TransactionType.SHOP_ORDER" :disabled="buttonText === '' || totalCurrenciesNotEnough.length > 1 || buttonDisabled" class="w-full hover:rounded-full hover:bg-white" expand="full" size="large" shape="round" @click="requiresLogin ? goToLogin() : handleCheckout(checkStock, formData)">
					<ion-spinner name="dots" v-if="buttonText === ''"></ion-spinner>
					<div v-else>{{ buttonText }}</div>
				</ion-button>
			</div>
		</div>
	</ion-content>
</template>

<style lang="scss" scoped></style>
